import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandPriceTierThresholds } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { TrendingUp, Search, Plus, Edit, Trash2, RefreshCcw, Filter, Eye, X, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, LayoutGrid, Target, DollarSign, Users, Clock, List, LayoutList, Check, Phone, Mail, Calendar, Briefcase, FileText } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import {
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  mapVentesStatCards,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  sliceVentesListItems,
  matchesVentesListDateRange,
  VentesConfigNotice,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useCrmConfig } from '../crm/useCrmConfig';
import { getCrmPipelineStages } from '../crm/crmConfigDefaults';
import { getCrmStageLabel, getCrmStageProbability } from '../../shared/utils/crmConstants';
import {
  buildCrmContactSelectOptions,
  buildFacturationClientSelectOptions,
  crmContactToPipelineForm,
  facturationClientToPipelineForm,
} from '../../shared/utils/crmCrossModuleUtils';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const PipelineCommercialPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ventes.pages.pipelineCommercialPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const priceTiers = getBrandPriceTierThresholds();

  const valueTierLabels = useMemo(() => ({
    high: p('dist_high_value', { amount: formatBrandMoney(priceTiers.highMin, false) }),
    medium: p('dist_medium_value', {
      low: formatBrandMoney(priceTiers.lowMax + 1, false),
      high: formatBrandMoney(priceTiers.highMin, false),
    }),
    low: p('dist_low_value', { amount: formatBrandMoney(priceTiers.lowMax, false) }),
  }), [p, priceTiers]);

  const [searchTerm, setSearchTerm] = useState('');
  const [opportunities, setOpportunities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const crmConfig = useCrmConfig();

  const [crmContacts, setCrmContacts] = useState([]);
  const [facturationClients, setFacturationClients] = useState([]);
  const [linkedPartyKey, setLinkedPartyKey] = useState('');
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  useCrudModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('opportunity');
  
  const [formData, setFormData] = useState({ 
    opportunity_name: '', 
    client_name: '',
    client_id: '',
    contact_person: '',
    email: '',
    phone: '',
    stage: 'lead',
    value: '',
    probability: '',
    expected_close_date: '',
    assigned_to: '',
    source: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_opportunities');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const pipelineFormTabs = useMemo(() => ([
    { id: 'opportunity', label: p('section_basic'), icon: Briefcase, iconColor: 'text-emerald-400' },
    { id: 'financial', label: p('section_financial'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'opportunity_name', label: p('col_opportunity'), required: true },
      { key: 'client_name', label: p('col_client') },
      { key: 'value', label: p('col_value') },
      { key: 'probability', label: p('col_probability') },
      { key: 'expected_close_date', label: p('col_close_date') },
      { key: 'stage', label: p('col_stage') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { loadOpportunities(); }, []);

  useEffect(() => {
    if (!showModal) return;
    let cancelled = false;
    (async () => {
      try {
        const [contactsRes, clientsRes] = await Promise.all([
          localApiAdapter.get('/crm/contacts'),
          localApiAdapter.get('/facturation/clients'),
        ]);
        if (cancelled) return;
        const contacts = Array.isArray(contactsRes) ? contactsRes : (contactsRes?.data || []);
        const clients = Array.isArray(clientsRes) ? clientsRes : (clientsRes?.data || clientsRes?.clients || []);
        setCrmContacts(contacts);
        setFacturationClients(clients);
      } catch (error) {
        console.error('Error loading CRM contacts / clients:', error);
        if (!cancelled) {
          setCrmContacts([]);
          setFacturationClients([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showModal]);

  useEffect(() => {
    if (!showModal || formData.client_id || !formData.client_name || facturationClients.length === 0) return;
    const matched = facturationClients.find(
      (c) => c.company_name === formData.client_name || c.name === formData.client_name,
    );
    if (matched) {
      setFormData((prev) => ({ ...prev, client_id: String(matched.id) }));
      setLinkedPartyKey(`cli:${matched.id}`);
    }
  }, [showModal, formData.client_id, formData.client_name, facturationClients]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOpportunities = async () => {
    try {
      const response = await localApiAdapter.get('/ventes/pipeline');
      const rows = response?.data || response || [];
      setOpportunities(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading opportunities:', error);
      setOpportunities([]);
    }
  };

  const stats = {
    total: opportunities.length,
    lead: opportunities.filter(o => o.stage === 'lead').length,
    qualified: opportunities.filter(o => o.stage === 'qualified').length,
    proposal: opportunities.filter(o => o.stage === 'proposal').length,
    negotiation: opportunities.filter(o => o.stage === 'negotiation').length,
    closed_won: opportunities.filter(o => o.stage === 'closed_won').length,
    closed_lost: opportunities.filter(o => o.stage === 'closed_lost').length,
    totalValue: opportunities.filter(o => o.stage !== 'closed_lost').reduce((sum, o) => sum + (parseFloat(o.value) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_opportunities', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_stage', name: p('tab_by_stage'), icon: Target },
    { id: 'by_value', name: p('tab_by_value'), icon: DollarSign },
  ]), [t, p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: t('common.all'), icon: Briefcase },
    { id: 'lead', name: p('filter_leads'), icon: Users },
    { id: 'active', name: p('filter_active'), icon: TrendingUp },
    { id: 'closing', name: p('filter_closing'), icon: Target },
  ]), [t, p]);

  const opportunityDistribution = useMemo(() => ({
    all_opportunities: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'prospects', name: p('dist_prospects'), count: stats.lead, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'qualified', name: p('dist_qualified'), count: stats.qualified, color: 'bg-gradient-to-r from-cyan-500 to-cyan-600' },
        { filterKey: 'proposal', name: p('dist_proposal'), count: stats.proposal, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'negotiation', name: p('dist_negotiation'), count: stats.negotiation, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'won', name: p('dist_won'), count: stats.closed_won, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    by_stage: {
      title: p('dist_by_stage_title'),
      data: [
        { filterKey: 'pipeline_start', name: p('dist_pipeline_start'), count: stats.lead + stats.qualified, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'pipeline_mid', name: p('dist_pipeline_mid'), count: stats.proposal + stats.negotiation, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'pipeline_end', name: p('dist_pipeline_end'), count: stats.closed_won + stats.closed_lost, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    by_value: {
      title: p('dist_by_value_title'),
      data: [
        { filterKey: 'high_value', name: valueTierLabels.high, count: opportunities.filter(o => parseFloat(o.value) > priceTiers.highMin).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'medium_value', name: valueTierLabels.medium, count: opportunities.filter(o => { const v = parseFloat(o.value) || 0; return v > priceTiers.lowMax && v <= priceTiers.highMin; }).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'low_value', name: valueTierLabels.low, count: opportunities.filter(o => (parseFloat(o.value) || 0) <= priceTiers.lowMax).length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      ],
    },
  }), [p, stats, opportunities, valueTierLabels, priceTiers]);

  const distributionInfo = opportunityDistribution[mainViewMode] || opportunityDistribution.all_opportunities;

  const stageFilterOptions = useMemo(() => ([
    { value: 'lead', label: p('stage_lead') },
    { value: 'qualified', label: p('stage_qualified') },
    { value: 'proposal', label: p('stage_proposal') },
    { value: 'negotiation', label: p('stage_negotiation') },
    { value: 'closed_won', label: p('stage_won') },
    { value: 'closed_lost', label: p('stage_lost') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('opportunity_name'),
    textColumnRule('client_name'),
    textColumnRule('value', (r) => String(r.value ?? '')),
    textColumnRule('probability', (r) => String(r.probability ?? '')),
    textColumnRule('expected_close_date', (r) => (r.expected_close_date ? new Date(r.expected_close_date).toLocaleDateString(dateLocale) : '')),
    exactColumnRule('stage', (r) => r.stage ?? ''),
  ]), [dateLocale]);

  const baseFilteredOpportunities = useMemo(() => opportunities.filter(o => {
    if (!matchesVentesListDateRange(o.expected_close_date, dateRange)) return false;
    // Search filter
    if (searchTerm && !o.opportunity_name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !o.client_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter
    if (mainViewMode === 'all_opportunities') {
      if (quickFilter === 'lead' && o.stage !== 'lead') return false;
      if (quickFilter === 'active' && !['qualified', 'proposal', 'negotiation'].includes(o.stage)) return false;
      if (quickFilter === 'closing' && !['negotiation', 'closed_won'].includes(o.stage)) return false;
    }
    
    // Distribution card filter
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_opportunities':
          if (activeDistributionFilter === 'prospects' && o.stage !== 'lead') return false;
          if (activeDistributionFilter === 'qualified' && o.stage !== 'qualified') return false;
          if (activeDistributionFilter === 'proposal' && o.stage !== 'proposal') return false;
          if (activeDistributionFilter === 'negotiation' && o.stage !== 'negotiation') return false;
          if (activeDistributionFilter === 'won' && o.stage !== 'closed_won') return false;
          break;
        case 'by_stage':
          if (activeDistributionFilter === 'pipeline_start' && !['lead', 'qualified'].includes(o.stage)) return false;
          if (activeDistributionFilter === 'pipeline_mid' && !['proposal', 'negotiation'].includes(o.stage)) return false;
          if (activeDistributionFilter === 'pipeline_end' && !['closed_won', 'closed_lost'].includes(o.stage)) return false;
          break;
        case 'by_value':
          const value = parseFloat(o.value) || 0;
          if (activeDistributionFilter === 'high_value' && value <= priceTiers.highMin) return false;
          if (activeDistributionFilter === 'medium_value' && (value <= priceTiers.lowMax || value > priceTiers.highMin)) return false;
          if (activeDistributionFilter === 'low_value' && value > priceTiers.lowMax) return false;
          break;
      }
    }
    
    return true;
  }), [opportunities, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, priceTiers, dateRange]);

  const filteredOpportunities = useMemo(
    () => applyColumnFilters(baseFilteredOpportunities, filters, columnFilterRules),
    [baseFilteredOpportunities, filters, columnFilterRules],
  );

  const filteredOpportunityIds = useMemo(() => filteredOpportunities.map((o) => o.id), [filteredOpportunities]);
  const displayedOpportunities = useMemo(
    () => sliceVentesListItems(filteredOpportunities, ventesConfig.itemsPerPage),
    [filteredOpportunities, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredOpportunityIds, selectedRecords),
    [filteredOpportunityIds, selectedRecords],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        opportunity_name: formData.opportunity_name,
        client_name: formData.client_name,
        contact_person: formData.contact_person || '',
        email: formData.email || '',
        phone: formData.phone || '',
        stage: formData.stage,
        value: parseFloat(formData.value) || 0,
        probability: parseFloat(formData.probability) || 0,
        expected_close_date: formData.expected_close_date || new Date().toISOString().slice(0, 10),
        assigned_to: formData.assigned_to || '',
        source: formData.source || '',
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/ventes/pipeline/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'فرصة' }));
      } else {
        await localApiAdapter.post('/ventes/pipeline', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'فرصة' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('unknown_error') }));
      return;
    } finally {
      resetForm();
      loadOpportunities();
    }
  };

  const handleEdit = (record) => {
    setFormTab('opportunity');
    setEditingRecord(record);
    const matchedClient = facturationClients.find(
      (c) => String(c.id) === String(record.client_id)
        || c.company_name === record.client_name
        || c.name === record.client_name,
    );
    setFormData({ 
      opportunity_name: record.opportunity_name || '',
      client_name: record.client_name || '',
      client_id: matchedClient ? String(matchedClient.id) : (record.client_id ? String(record.client_id) : ''),
      contact_person: record.contact_person || '',
      email: record.email || '',
      phone: record.phone || '',
      stage: record.stage || 'lead',
      value: record.value?.toString() || '',
      probability: record.probability?.toString() || '',
      expected_close_date: record.expected_close_date ? record.expected_close_date.substring(0, 10) : '',
      assigned_to: record.assigned_to || '',
      source: record.source || '',
      notes: record.notes || ''
    });
    setLinkedPartyKey(matchedClient ? `cli:${matchedClient.id}` : '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/ventes/pipeline/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'فرصة' }));
        loadOpportunities();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف الفرصة' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/ventes/pipeline/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'فرصة' }));
        setSelectedRecords([]);
        loadOpportunities();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف الفرص المحددة' }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      opportunity_name: '', 
      client_name: '',
      client_id: '',
      contact_person: '',
      email: '',
      phone: '',
      stage: 'lead',
      value: '',
      probability: '',
      expected_close_date: '',
      assigned_to: '',
      source: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('opportunity');
    setLinkedPartyKey('');
    setShowModal(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredOpportunityIds);
    }
  };
  
  const handleSelectRecord = (id) => { 
    setSelectedRecords(selectedRecords.includes(id) ? selectedRecords.filter(rId => rId !== id) : [...selectedRecords, id]); 
  };

  const stageOptions = useMemo(() => getCrmPipelineStages(crmConfig).map((s) => ({
    value: s.value,
    label: getCrmStageLabel(s.value, t),
    icon: s.value === 'lead' ? Users
      : s.value === 'qualified' ? Check
      : s.value === 'proposal' ? FileText
      : s.value === 'negotiation' ? TrendingUp
      : s.value === 'closed_won' ? Check
      : X,
    style: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  })), [crmConfig, t]);

  const linkedPartyOptions = useMemo(() => [
    ...buildFacturationClientSelectOptions(facturationClients),
    ...buildCrmContactSelectOptions(crmContacts),
  ], [facturationClients, crmContacts]);

  const clientSelectOptions = useMemo(
    () => facturationClients.map((client) => ({
      value: String(client.id),
      label: `${client.company_name || client.name || p('col_client')}${client.contact_person ? ` — ${client.contact_person}` : ''}${client.phone1 ? ` — ${client.phone1}` : ''}`,
    })),
    [facturationClients, p],
  );

  const handleClientSelection = (clientId) => {
    if (!clientId) {
      setFormData((prev) => ({ ...prev, client_id: '', client_name: '' }));
      setLinkedPartyKey('');
      return;
    }
    const client = facturationClients.find((c) => String(c.id) === String(clientId));
    if (client) {
      setFormData((prev) => facturationClientToPipelineForm(client, { ...prev, client_id: String(clientId) }));
      setLinkedPartyKey(`cli:${clientId}`);
    }
  };

  const handleLinkedPartyChange = (key) => {
    setLinkedPartyKey(key || '');
    if (!key) return;
    if (key.startsWith('cli:')) {
      const client = facturationClients.find((c) => String(c.id) === key.slice(4));
      setFormData((prev) => facturationClientToPipelineForm(client, { ...prev, client_id: key.slice(4) }));
      return;
    }
    if (key.startsWith('crm:')) {
      const contact = crmContacts.find((c) => String(c.id) === key.slice(4));
      setFormData((prev) => crmContactToPipelineForm(contact, prev));
    }
  };

  const handleStageChange = async (recordId, newStage) => {
    try {
      const payload = { stage: newStage };
      if (crmConfig.autoMoveStages) {
        payload.probability = getCrmStageProbability(newStage);
      }
      await localApiAdapter.put(`/ventes/pipeline/${recordId}`, payload);
      await loadOpportunities();
    } catch (error) {
      console.error('❌ Error updating stage:', error);
    }
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Briefcase, variant: 'copper' },
      { key: 'leads', label: p('stat_leads'), value: stats.lead, icon: Users, variant: 'forest' },
      { key: 'active', label: p('stat_active'), value: stats.qualified + stats.proposal + stats.negotiation, icon: TrendingUp, variant: 'copper' },
      {
        key: 'won',
        label: p('stat_won'),
        value: stats.closed_won,
        icon: Check,
        variant: 'forest',
        className: stats.totalValue >= (ventesConfig.defaultSalesTarget ?? 0) ? 'ring-1 ring-emerald-400/40' : undefined,
      },
    ]),
    [stats, p, ventesConfig.defaultSalesTarget],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_opportunities' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <X size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_by_title', { name: item.name })}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}% {t('common.of_total')}</p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={TrendingUp}
        message={ventesConfig.enablePipeline === false ? p('notice_pipeline_disabled', { defaultValue: 'المسار التجاري معطّل — فعّله من إعدادات المبيعات ← العمولات ← العرض.' }) : null}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={ventesConfig.enablePipeline !== false ? () => { setEditingRecord(null); setFormTab('opportunity'); setShowModal(true); } : undefined}
        createLabel={p('new_opportunity')}
        importExportState={{ showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown, importDropdownRef, exportDropdownRef }}
        importExportHandlers={{}}
        isGeneratingPDF={isGeneratingPDF}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        extraHeaderActions={(
          <button type="button" onClick={loadOpportunities} className="refresh-button" title={t('common.refresh')}>
            <RefreshCcw size={20} />
          </button>
        )}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                      />
                    </div>
                  </th>
                  
                  <TableColumnHeaderFilter columnKey="opportunity_name" label={p('col_opportunity')} value={filters.opportunity_name || ''} onChange={(v) => setFilter('opportunity_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('client_name') ? (
                    <TableColumnHeaderFilter columnKey="client_name" label={p('col_client')} value={filters.client_name || ''} onChange={(v) => setFilter('client_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('value') ? (
                    <TableColumnHeaderFilter columnKey="value" label={p('col_value')} value={filters.value || ''} onChange={(v) => setFilter('value', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('probability') ? (
                    <TableColumnHeaderFilter columnKey="probability" label={p('col_probability')} value={filters.probability || ''} onChange={(v) => setFilter('probability', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('expected_close_date') ? (
                    <TableColumnHeaderFilter columnKey="expected_close_date" label={p('col_close_date')} value={filters.expected_close_date || ''} onChange={(v) => setFilter('expected_close_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('stage') ? (
                    <TableColumnHeaderFilter columnKey="stage" label={p('col_stage')} kind="select" options={stageFilterOptions} value={filters.stage || ''} onChange={(v) => setFilter('stage', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedOpportunities.length > 0 ? (
                  displayedOpportunities.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.opportunity_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.contact_person || p('no_contact')}</span>
                        </div>
                      </td>
                      {showCol('client_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.client_name}</span>
                            <span className="text-white/50 text-xs mt-0.5">{record.email || record.phone || p('no_info')}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('value') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney(record.value || 0)}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('estimated_value')}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('probability') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.probability || 0}%</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('success_chance')}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('expected_close_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {record.expected_close_date ? new Date(record.expected_close_date).toLocaleDateString(dateLocale) : '-'}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.assigned_to || p('unassigned')}
                            </span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('stage') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.stage}
                              recordId={record.id}
                              options={stageOptions}
                              onChange={handleStageChange}
                              title={p('click_change_stage')}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Briefcase size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedOpportunities.length > 0 ? (
              displayedOpportunities.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <TrendingUp size={20} className="text-emerald-400" />
                          {record.opportunity_name}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={record.stage}
                          recordId={record.id}
                          options={stageOptions}
                          onChange={handleStageChange}
                          title={p('click_change_stage')}
                        />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {formatBrandMoney(record.value || 0)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_client_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_client')}</span> {record.client_name}</div>
                            <div><span className="font-medium">{p('label_contact')}</span> {record.contact_person || '-'}</div>
                            <div><span className="font-medium">{p('label_email')}</span> {record.email || '-'}</div>
                            <div><span className="font-medium">{p('label_phone')}</span> {record.phone || '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_financial')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-green-400" />
                              <span className="font-medium">{p('label_value')}</span> <span className="text-white">{formatBrandMoney(record.value || 0)}</span>
                            </div>
                            <div><span className="font-medium">{p('label_probability')}</span> {record.probability || 0}%</div>
                            <div><span className="font-medium">{p('label_close_date')}</span> {record.expected_close_date ? new Date(record.expected_close_date).toLocaleDateString(dateLocale) : '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_management')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_assigned')}</span> {record.assigned_to || '-'}</div>
                            <div><span className="font-medium">{p('label_source')}</span> {record.source || '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Briefcase size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        maxWidth="1100px"
        testId="pipeline-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase size={18} className="text-emerald-400" />
                    {p('section_general')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_opportunity')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.opportunity_name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_client')}</p>
                      <p className="text-white font-medium">{viewingRecord.client_name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_contact_field')}</p>
                      <p className="text-white font-medium">{viewingRecord.contact_person || '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_stage')}</p>
                      <div className="flex">
                        <QawaemClickableStatusBadge
                          status={viewingRecord.stage}
                          recordId={viewingRecord.id}
                          options={stageOptions}
                          onChange={handleStageChange}
                          title={p('click_change_stage')}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-green-400" />
                      {p('section_financial')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_value')}</p>
                        <p className="text-white font-medium">{formatBrandMoney(viewingRecord.value || 0)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_probability')}</p>
                        <p className="text-white font-medium">{viewingRecord.probability || 0}%</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_expected_close')}</p>
                        <p className="text-white font-medium">{viewingRecord.expected_close_date ? new Date(viewingRecord.expected_close_date).toLocaleDateString(dateLocale) : '-'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Users size={18} className="text-blue-400" />
                      Gestion
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_assigned_field')}</p>
                        <p className="text-white font-medium">{viewingRecord.assigned_to || '-'}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_source_field')}</p>
                        <p className="text-white font-medium">{viewingRecord.source || '-'}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_email_field')}</p>
                        <p className="text-white font-medium">{viewingRecord.email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_phone_field')}</p>
                        <p className="text-white font-medium">{viewingRecord.phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-purple-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={TrendingUp}
        showSaveButton
        formId="opportunity-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="pipeline-crud-modal"
        tabs={pipelineFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
      >
        <form id="opportunity-form" onSubmit={handleSubmit}>
          {formTab === 'opportunity' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('modules.crm.pages.contactsPage.title', { defaultValue: 'جهة اتصال CRM / عميل' })}</label>
                <CustomSelect
                  value={linkedPartyKey}
                  onChange={handleLinkedPartyChange}
                  options={linkedPartyOptions}
                  placeholder={t('modules.crm.pages.contactsPage.search_placeholder', { defaultValue: 'اختر من CRM أو العملاء…' })}
                  searchable
                />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_opportunity_name')} <span className="qawaem-required-mark">*</span></label>
                <input type="text" required value={formData.opportunity_name} onChange={(e) => setFormData({ ...formData, opportunity_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_opportunity')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_client_name')} <span className="qawaem-required-mark">*</span></label>
                <CustomSelect
                  value={formData.client_id}
                  onChange={handleClientSelection}
                  options={clientSelectOptions}
                  placeholder={p('ph_client')}
                  searchable
                  required
                />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_stage')}</label>
                <CustomSelect
                  value={formData.stage}
                  onChange={(value) => {
                    const next = { ...formData, stage: value };
                    if (crmConfig.autoMoveStages) {
                      next.probability = String(getCrmStageProbability(value));
                    }
                    setFormData(next);
                  }}
                  options={stageOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))}
                  placeholder={p('ph_stage')}
                />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_close_date')}</label>
                <CustomDatePicker value={formData.expected_close_date} onChange={(date) => setFormData({ ...formData, expected_close_date: date })} placeholder={p('ph_date')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_assigned')}</label>
                <input type="text" value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_assigned')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_contact_person')}</label>
                <input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_contact')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.email')}</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_email')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.phone')}</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_phone')} />
              </div>
            </div>
          )}
          {formTab === 'financial' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_value')}</label>
                <CustomNumberInput value={formData.value} onChange={(value) => setFormData({ ...formData, value: value })} placeholder={p('placeholder_zero')} step={1000} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_probability')}</label>
                <CustomNumberInput value={formData.probability} onChange={(value) => setFormData({ ...formData, probability: value })} placeholder={p('placeholder_zero')} step={5} min={0} max={100} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_source')}</label>
                <input type="text" value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_source')} />
              </div>
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} />
              </div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default PipelineCommercialPage;

